import React, { useState, useRef, useCallback } from 'react';

import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';

import usePrepareForRequestColumns from './columns';
import TableFilter from './filters';
import TableBody from './TableBody';
import EditItemRequestDialog from './UpdateItemDialog/EditItemRequestDialog';
import useTable from './useTable';

interface ItemsForRequestTableProps {
  items: IOneItemInPrepareForRequest[];
  handleShowCompetitors: (value: boolean) => void;
  handleShowItemGroup: (value: boolean) => void;
  handleShowUnderRequest: (value: boolean) => void;
  handleShowPurchaseHistory: (value: boolean) => void;
  handleSelectItem: (item: IOneItemInPrepareForRequest) => void;
  handleUpdateOneItemLink: (
    item: IOneItemInPrepareForRequest,
    skipResetPage?: boolean,
  ) => void;
  setItems: React.Dispatch<React.SetStateAction<IOneItemInPrepareForRequest[]>>;
  handleLoadingStage: (
    value: boolean,
    type: 'check' | 'upload' | 'update' | 'main',
  ) => void;
  isLoading: boolean;
}

interface EditDialogState {
  item: IOneItemInPrepareForRequest | null;
  isOpen: boolean;
}

function ItemsForRequestTable({
  items,
  handleShowCompetitors,
  handleSelectItem,
  handleShowItemGroup,
  handleUpdateOneItemLink,
  handleLoadingStage,
  isLoading,
  setItems,
  handleShowUnderRequest,
  handleShowPurchaseHistory,
}: ItemsForRequestTableProps) {
  const tableContainerRef = useRef<HTMLDivElement>(null!);

  const [editItemDialog, setEditItemDialog] = useState<EditDialogState>({
    item: null,
    isOpen: false,
  });

  const handleOpenEditDialog = useCallback(
    (item: IOneItemInPrepareForRequest) => {
      setEditItemDialog({
        item,
        isOpen: true,
      });
    },
    [],
  );

  const handleCloseEditDialog = useCallback(() => {
    setEditItemDialog({
      item: null,
      isOpen: false,
    });
  }, []);

  // Create handlers object for columns
  const columnHandlers = {
    handleOpenEditDialog,
    handleSelectItem,
    handleShowUnderRequest,
    handleShowCompetitors,
    handleShowItemGroup,
    handleShowPurchaseHistory,
    handleUpdateOneItemLink,
    handleLoadingStage,
  };

  // Get columns
  const columns = usePrepareForRequestColumns(columnHandlers);

  // Create table config
  const tableConfig = {
    items,
    setItems,
    columns,
  };

  // Get table instance
  const table = useTable(tableConfig);

  // Create a wrapper for handleUpdateOneItemLink that uses skipAutoResetPageIndex when needed
  const handleUpdateItemWithPagination = useCallback(
    (item: IOneItemInPrepareForRequest, skipResetPage?: boolean) => {
      if (skipResetPage && table.options.meta?.skipAutoResetPageIndex) {
        table.options.meta.skipAutoResetPageIndex();
      }
      handleUpdateOneItemLink(item, skipResetPage);
    },
    [handleUpdateOneItemLink, table.options.meta],
  );

  return (
    <div className="w-full">
      <TableFilter table={table}>
        <TableBody
          tableContainerRef={tableContainerRef}
          columns={columns}
          items={items}
          table={table}
        />
      </TableFilter>

      {editItemDialog.isOpen && editItemDialog.item && (
        <EditItemRequestDialog
          item={editItemDialog.item}
          isOpen={editItemDialog.isOpen}
          closeFn={handleCloseEditDialog}
          handleUpdateOneItemLink={handleUpdateItemWithPagination}
          handleLoadingStage={handleLoadingStage}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

export default ItemsForRequestTable;
